import { API_URL, API_SECRET, APP_VERSION, DEVICE_BOOTSTRAP_URL } from "../config/appConfig";
import { FARM_COLLECTIONS } from "../admin/adminData";
import { getDeviceId, DB } from "../lib/deviceStorage";
import { shadowKey, verKey, cursorKey, deltaCursorKey, opsKey, pendingKey, appendSyncHistory, stripDeletedItems, stableJson } from "../lib/syncUtils";
import { SyncQueue } from "./syncQueue";
import { fetchWithTimeout, createIdempotencyKey } from "./apiClient";
import { setLiveCollectionCache } from "./cacheApi";
import { safeLocalStorageSetItem } from "../lib/storageQuotaRecovery";


// v3.10.00: gate de bootstrap inicial autoritativo pelo servidor (device+fazenda).
export const BOOTSTRAP_SCHEMA_VERSION = 1;

async function deviceBootstrapApiCall(body, userName = "", timeoutMs = 12000) {
  const resp = await fetchWithTimeout(DEVICE_BOOTSTRAP_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": API_SECRET,
      "X-Device-ID": getDeviceId(),
      "X-User-Name": userName || "",
    },
    body: JSON.stringify({
      schema_version: BOOTSTRAP_SCHEMA_VERSION,
      app_version: APP_VERSION,
      ...body,
    }),
  }, timeoutMs);
  const json = await resp.json().catch(() => ({}));
  if (!json.ok && !json.data) {
    const err = new Error(json.error || `Device bootstrap API error ${resp.status}`);
    err.payload = json;
    err.status = resp.status;
    throw err;
  }
  return json.data ?? json;
}

async function checkDeviceBootstrapState(farmId, userName = "", options = {}) {
  if (!farmId) return { required: true, status: "not_started", reason: "no_farm" };
  try {
    return await deviceBootstrapApiCall({
      action: "check",
      farm_id: farmId,
      account_id: options?.accountId || options?.account_id || "",
      username: options?.username || userName || "",
    }, userName, 12000);
  } catch (error) {
    return { required: true, status: "unknown", reason: "check_failed", error: String(error?.message || error || "check_failed") };
  }
}

async function startDeviceBootstrapState(farmId, userName = "", options = {}) {
  if (!farmId) return { skipped: true, reason: "no_farm" };
  try {
    return await deviceBootstrapApiCall({
      action: "start",
      farm_id: farmId,
      account_id: options?.accountId || options?.account_id || "",
      username: options?.username || userName || "",
    }, userName, 12000);
  } catch (error) {
    return { skipped: true, reason: "start_failed", error: String(error?.message || error || "start_failed") };
  }
}

async function completeDeviceBootstrapState(farmId, userName = "", options = {}) {
  if (!farmId) return { skipped: true, reason: "no_farm" };
  try {
    return await deviceBootstrapApiCall({
      action: "complete",
      farm_id: farmId,
      account_id: options?.accountId || options?.account_id || "",
      username: options?.username || userName || "",
      completed_collections: Array.isArray(options?.completedCollections) ? options.completedCollections : [],
    }, userName, 15000);
  } catch (error) {
    return { skipped: true, reason: "complete_failed", error: String(error?.message || error || "complete_failed") };
  }
}

async function failDeviceBootstrapState(farmId, userName = "", error = "bootstrap_failed", options = {}) {
  if (!farmId) return { skipped: true, reason: "no_farm" };
  try {
    return await deviceBootstrapApiCall({
      action: "fail",
      farm_id: farmId,
      account_id: options?.accountId || options?.account_id || "",
      username: options?.username || userName || "",
      error: String(error || "bootstrap_failed"),
    }, userName, 12000);
  } catch (_error) {
    return { skipped: true, reason: "fail_report_failed" };
  }
}

const INSTALL_KEY = "fz:server_install_id";
const INSTALL_ACK_KEY = "fz:server_install_ack";
const INSTALL_LAST_CHECK_KEY = "fz:server_install_last_check";
const INSTALL_NOTICE_KEY = "fz:server_install_notice";
const INSTALL_RETRY_AFTER_ERROR_MS = 5 * 60 * 1000;
let installCheckPromise = null;
let confirmedInstallId = null;
let lastInstallCheckAt = 0;
let installBackoffUntil = 0;


// v3.8.10: pull_global não pode virar polling agressivo por render/módulo.
// Deduplica chamadas em voo e aplica TTL curto por key+usuário. pushGlobal invalida a key.
const GLOBAL_PULL_CACHE_TTL_MS = 2 * 60 * 1000;
const GLOBAL_PULL_SHORT_TTL_MS = 45 * 1000;
const globalPullCache = new Map();
const globalPullInFlight = new Map();

function globalPullTtlForKey(key) {
  const clean = String(key || "");
  if (clean === "fz:security:autoLogout") return GLOBAL_PULL_SHORT_TTL_MS;
  // v3.9.13: fz:maintenance é checado a cada 120s pelo useMaintenanceInfo —
  // TTL de 90s evita buscas redundantes pelo scheduler global.
  if (clean === "fz:maintenance") return 90 * 1000;
  if (clean === "auth:accounts" || clean === "fz:farms") return GLOBAL_PULL_SHORT_TTL_MS;
  return GLOBAL_PULL_CACHE_TTL_MS;
}

function globalPullCacheKey(key, userName) {
  return `${String(userName || "system")}::${String(key || "")}`;
}

function invalidateGlobalPullCache(key = "") {
  const clean = String(key || "");
  for (const cacheKey of Array.from(globalPullCache.keys())) {
    if (!clean || cacheKey.endsWith(`::${clean}`)) globalPullCache.delete(cacheKey);
  }
  for (const cacheKey of Array.from(globalPullInFlight.keys())) {
    if (!clean || cacheKey.endsWith(`::${clean}`)) globalPullInFlight.delete(cacheKey);
  }
}

// v3.9.04: gate central de rede durante bootstrap exclusivo.
// Bloqueia apenas chamadas para sync.php que concorrem com o bundle.
// Não bloqueia: device-registry, ping, login/auth, APIs nativas de segurança.
function _shouldBlockDuringBootstrap(body) {
  let _ex = null;
  try { _ex = typeof window !== "undefined" ? window.__fzBootstrapExclusiveMode : null; } catch (_) {}
  if (!_ex?.active) return false;

  const _action = String(body?.action || "").trim();
  const _farmId = String(body?.farm_id || body?.farmId || "").trim();
  const _phase = String(_ex.phase || "snapshot");

  // v3.9.09: fazenda já marcada como ready → nunca bloquear, mesmo se o
  // exclusive mode ficou preso por bug de sessão anterior.
  try {
    const _readyMap = typeof window !== "undefined" ? window.__fzBootstrapReadyByFarm : null;
    if (_farmId && _readyMap?.[_farmId]) return false;
  } catch (_) {}

  // O bundle do próprio bootstrap sempre permitido.
  if (_action === "pull_farm_bundle" && body?.bootstrap_source === "protected_bootstrap") return false;

  // Durante pós-delta interno: revision_check e pull_delta permitidos.
  if (_phase === "post_delta" && (_action === "revision_check" || _action === "pull_delta")) return false;

  // Actions de segurança/identidade: nunca bloquear.
  const _allowed = new Set([
    "get_install_state", "ensure_install", "check_install",
    "check_device_status", "register_device", "regenerate_code",
    "approve_device", "revoke_device", "rename_device",
    "archive_device", "list_devices", "device_registry",
    "login", "logout", "session", "auth",
    "ping",
  ]);
  if (_allowed.has(_action)) return false;

  // v3.9.05: pull_global comum compete com o bundle e fica pausado durante snapshot.
  if (_action === "pull_global") {
    const _key      = String(body?.key || "").trim();
    const _exFarmId = String(_ex?.farmId || "").trim();

    // v3.9.10: request sem farm_id = prelogin (fz:farms, auth:accounts, etc).
    // O quiet mode só faz sentido para requests da farm específica em bootstrap.
    // Prelogin não tem farm_id → nunca bloquear.
    if (!_farmId) return false;

    // Request de farm diferente da que está em bootstrap → não bloquear.
    if (_exFarmId && _farmId !== _exFarmId) return false;

    const _allowedGlobalKeysDuringSnapshot = new Set([
      "fz:install",
      "fz:session",
      "fz:native:session",
      "fz:native:device",
      // v3.9.10: essenciais para prelogin e cold start — nunca bloquear.
      "fz:farms",
      "auth:accounts",
      "fz:accounts",
    ]);
    if (_phase === "snapshot" && !_allowedGlobalKeysDuringSnapshot.has(_key)) {
      try {
        console.warn("[v3.9.10] pull_global bloqueado durante bootstrap snapshot", {
          key: _key, farmId: _farmId, exFarmId: _exFarmId,
        });
      } catch (_) {}
      return true;
    }
  }

  // v3.9.10: durante snapshot, bloquear apenas requests da farm em bootstrap.
  // Requests sem farm_id (prelogin) ou de outra farm passam livremente.
  const _exFarmId2 = String(_ex?.farmId || "").trim();
  if (!_farmId) return false;
  if (_exFarmId2 && _farmId !== _exFarmId2) return false;
  return true;
}

async function rawApiCall(body, userName="", timeoutMs = 8000) {
  // v3.9.04: bloquear chamadas sync.php que competem com o bootstrap exclusivo.
  if (_shouldBlockDuringBootstrap(body)) {
    const _action = String(body?.action || "");
    const _col = String(body?.collection || "");
    const _farmId = String(body?.farm_id || body?.farmId || "").trim();
    let _phase = "snapshot";
    try { _phase = window.__fzBootstrapExclusiveMode?.phase || "snapshot"; } catch (_) {}
    try {
      console.warn("[v3.9.10] sync bloqueado — bootstrap exclusivo ativo", {
        action: _action,
        collection: _col,
        farmId: _farmId,
        phase: _phase,
        ready: !!window.__fzBootstrapReadyByFarm?.[_farmId],
      });
    } catch (_) {}
    return { skipped: true, reason: "bootstrap_exclusive_network_gate", action: _action, collection: _col, farmId: _farmId };
  }
  const resp = await fetchWithTimeout(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key":    API_SECRET,
      "X-Device-ID":  getDeviceId(),
      "X-User-Name":  userName,
    },
    body: JSON.stringify(body),
  }, timeoutMs);
  const json = await resp.json().catch(() => ({}));
  if (!json.ok && !json.data) {
    const err = new Error(json.error || `API error ${resp.status}`);
    err.payload = json;
    err.status = resp.status;
    throw err;
  }
  return json.data ?? json;
}

async function clearLocalStateForServerInstall(serverInstallId) {
  const deviceId = getDeviceId();
  // v2.220.03: preservar identidade local do app nativo durante troca de install_id.
  // A limpeza por nova instalação deve descartar caches/sync, mas não deve deslogar
  // o usuário nem recriar device_id, pois isso quebra o vínculo APNs/Live Activities.
  let savedSession = null;
  let savedActiveFarm = null;
  let savedDeviceId = deviceId;
  // v3.9.04: preservar identidade nativa/device registry em qualquer limpeza local.
  // O bootstrap abortado/resetado não pode apagar vínculo Keychain/bridge nem status do device.
  const preservedNative = {};
  const preservedNativeKeys = [
    "fz:device_registry_status",
    "fz:device_registry_code",
    "fz:native_device_verified",
    "fz:session_id",
  ];
  try { savedSession = localStorage.getItem("fz:session"); } catch {}
  try { savedActiveFarm = localStorage.getItem("fz:activeFarm"); } catch {}
  try { savedDeviceId = localStorage.getItem("fz:device_id") || deviceId; } catch {}
  for (const key of preservedNativeKeys) {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) preservedNative[key] = value;
    } catch {}
  }
  const notice = "Nova instalação detectada pelo banco de dados. Os dados locais anteriores foram descartados e o app será carregado somente com os dados atuais do servidor.";
  try { sessionStorage.clear(); } catch {}
  try { localStorage.clear(); } catch {}
  try { if (savedSession) safeLocalStorageSetItem("fz:session", savedSession, { silent: true }); } catch {}
  try { if (savedActiveFarm) safeLocalStorageSetItem("fz:activeFarm", savedActiveFarm, { silent: true }); } catch {}
  try { safeLocalStorageSetItem("fz:device_id", savedDeviceId || deviceId, { silent: true }); } catch {}
  for (const [key, value] of Object.entries(preservedNative)) {
    try { safeLocalStorageSetItem(key, value, { silent: true }); } catch {}
  }
  try { safeLocalStorageSetItem(INSTALL_KEY, serverInstallId, { silent: true }); } catch {}
  try { safeLocalStorageSetItem(INSTALL_ACK_KEY, serverInstallId, { silent: true }); } catch {}
  try { safeLocalStorageSetItem(INSTALL_LAST_CHECK_KEY, String(Date.now()), { silent: true }); } catch {}
  try { safeLocalStorageSetItem(INSTALL_NOTICE_KEY, notice, { silent: true }); } catch {}
  try {
    if (typeof indexedDB !== "undefined") {
      indexedDB.deleteDatabase("fazenda-hostgator-offline");
    }
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent("fz:clean-install-reset", { detail: { serverInstallId, notice } }));
  } catch {}
  setTimeout(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("cleanInstall");
      url.searchParams.delete("forceInstallCheck");
      url.searchParams.set("installChanged", String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  }, 250);
}

function getCachedInstallState() {
  try {
    const localInstallId = localStorage.getItem(INSTALL_KEY) || "";
    const acknowledged = localStorage.getItem(INSTALL_ACK_KEY) || "";
    const lastCheck = Number(localStorage.getItem(INSTALL_LAST_CHECK_KEY) || 0);
    return { localInstallId, acknowledged, lastCheck };
  } catch (e) {
    return { localInstallId: "", acknowledged: "", lastCheck: 0 };
  }
}

function rememberConfirmedInstall(serverInstallId) {
  if (!serverInstallId) return;
  confirmedInstallId = String(serverInstallId);
  lastInstallCheckAt = Date.now();
  try { safeLocalStorageSetItem(INSTALL_KEY, confirmedInstallId, { silent: true }); } catch {}
  try { safeLocalStorageSetItem(INSTALL_ACK_KEY, confirmedInstallId, { silent: true }); } catch {}
  try { safeLocalStorageSetItem(INSTALL_LAST_CHECK_KEY, String(lastInstallCheckAt), { silent: true }); } catch {}
}

function shouldForceInstallRecheck() {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("forceInstallCheck") || url.searchParams.get("cleanInstall") || "";
    if (!token) return false;
    const key = `fz:install_check_url_token:${APP_VERSION}`;
    if (sessionStorage.getItem(key) === token) return false;
    sessionStorage.setItem(key, token);
    return true;
  } catch (e) {
    return false;
  }
}

async function handleInstallMismatch(serverInstallId) {
  if (serverInstallId) {
    await clearLocalStateForServerInstall(String(serverInstallId));
  }
}

async function ensureServerInstall(userName="", options = {}) {
  const force = !!options?.force || shouldForceInstallRecheck();
  const now = Date.now();
  const { localInstallId, acknowledged, lastCheck } = getCachedInstallState();

  // Caminho quente: depois que a instalação foi confirmada, não bater no sync.php
  // a cada pull/push. A proteção continua existindo porque o backend rejeita push
  // com install_id antigo e este módulo trata o 409 como mismatch real.
  if (!force) {
    if (confirmedInstallId) return confirmedInstallId;
    if (localInstallId && acknowledged === localInstallId) {
      confirmedInstallId = localInstallId;
      lastInstallCheckAt = lastCheck || now;
      return confirmedInstallId;
    }
    if (installBackoffUntil > now && localInstallId && acknowledged === localInstallId) {
      confirmedInstallId = localInstallId;
      return confirmedInstallId;
    }
  }

  if (installCheckPromise) return installCheckPromise;

  installCheckPromise = (async () => {
    try {
      const state = await rawApiCall({ action:"get_install_state", server_install_id: localInstallId, app_version: APP_VERSION }, userName);
      // v3.9.13: se rawApiCall retornou skipped (ex: gate de bootstrap), tratar como
      // confirmação local temporária — não tentar de novo imediatamente.
      if (state?.skipped) {
        if (localInstallId && acknowledged === localInstallId) {
          confirmedInstallId = localInstallId;
          return confirmedInstallId;
        }
        return localInstallId || "";
      }
      const serverInstallId = String(state?.install_id || "");
      if (!serverInstallId) return localInstallId || "";
      if (!localInstallId) {
        rememberConfirmedInstall(serverInstallId);
        return serverInstallId;
      }
      if (localInstallId === serverInstallId) {
        rememberConfirmedInstall(serverInstallId);
        return serverInstallId;
      }
      await clearLocalStateForServerInstall(serverInstallId);
      throw new Error("Nova instalação detectada pelo banco de dados. Limpando dados locais antigos.");
    } catch (e) {
      const status = Number(e?.status || e?.payload?.status || 0);
      const payload = e?.payload || {};
      const serverInstallId = String(payload?.server_install_id || payload?.install_id || payload?.data?.install_id || "");
      if (status === 409 && serverInstallId) {
        await handleInstallMismatch(serverInstallId);
      }
      if ((status === 429 || status === 503 || String(e?.message || "").includes("timeout")) && localInstallId && acknowledged === localInstallId) {
        installBackoffUntil = Date.now() + INSTALL_RETRY_AFTER_ERROR_MS;
        confirmedInstallId = localInstallId;
        return confirmedInstallId;
      }
      throw e;
    } finally {
      installCheckPromise = null;
    }
  })();

  return installCheckPromise;
}

async function apiCall(body, userName="") {
  const action = String(body?.action || "");
  if (action !== "get_install_state") {
    const serverInstallId = await ensureServerInstall(userName);
    body = { ...body, server_install_id: serverInstallId };
  }
  try {
    return await rawApiCall(body, userName);
  } catch (e) {
    const status = Number(e?.status || 0);
    const payload = e?.payload || {};
    const serverInstallId = String(payload?.server_install_id || payload?.install_id || payload?.data?.install_id || "");
    if (status === 409 && serverInstallId) {
      await handleInstallMismatch(serverInstallId);
    }
    throw e;
  }
}
// Push one collection to server
async function pushCollection(farmId, collection, data, localVersion, userName) {
  const shadow = (await DB.get(shadowKey(farmId, collection))) || [];
  const enriched = appendSyncHistory(shadow, Array.isArray(data) ? data : [], userName || "system", "push");
  const idemKey = createIdempotencyKey(farmId, collection, localVersion, enriched);
  return await apiCall({
    action:"push",
    farm_id:farmId,
    collection,
    data: enriched,
    version: localVersion,
    base_version: localVersion,
    idempotency_key: idemKey,
    client_digest: stableJson(enriched),
    app_version: APP_VERSION,
  }, userName);
}

// v3.5.00: push por operação para módulos operacionais migrados — sem coleção inteira.
// Envia apenas opsToPush. Timeout 15s (payload pequeno, sem risco de timeout por coleção).
async function pushOperationalOps(farmId, collection, ops, userName) {
  const cleanCollection = String(collection || "orders");
  // v3.9.16: push_ops usa fetch direto e informa farm_id ao gate para
  // distinguir farm ready/bootstrap com precisão.
  if (_shouldBlockDuringBootstrap({ action: "push_ops", farm_id: farmId, collection: cleanCollection })) {
    return { skipped: true, reason: "bootstrap_exclusive_network_gate", action: "push_ops", collection: cleanCollection, farmId };
  }
  if (!Array.isArray(ops) || ops.length === 0) throw new Error("pushOperationalOps: ops vazio");
  const normalizedOps = ops.map(op => ({
    op_id:        String(op?.opId || op?.pendingOperationId || op?.payload?.pendingOperationId || ""),
    operation_id: String(op?.pendingOperationId || op?.operationId || op?.payload?.pendingOperationId || op?.payload?.localOperationId || op?.opId || ""),
    type:         String(op?.type || "edit"),
    item_id:      String(op?.itemId || op?.payload?.clientId || op?.payload?.id || ""),
    payload:      op?.payload || {},
    base_ts:      op?.baseTs ?? 0,
    ts:           op?.ts || new Date().toISOString(),
    user_name:    userName || "unknown",
  }));
  const resp = await fetchWithTimeout(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key":    API_SECRET,
      "X-Device-ID":  getDeviceId(),
      "X-User-Name":  userName || "",
    },
    body: JSON.stringify({
      action:      "push_ops",
      farm_id:     farmId,
      collection:  cleanCollection,
      ops:         normalizedOps,
      app_version: APP_VERSION,
    }),
  }, 15000);
  const json = await resp.json().catch(() => ({}));
  if (!json.ok && !json.data) {
    const err = new Error(json.error || `push_ops ${cleanCollection} error ${resp.status}`);
    err.payload = json;
    err.status  = resp.status;
    throw err;
  }
  return json.data ?? json;
}

async function pushOrderOps(farmId, ops, userName) {
  return await pushOperationalOps(farmId, "orders", ops, userName);
}

// v3.4.04: endpoint leve de revision. Retorna { revisions: { orders: N } }.
// Usado pelo scheduler operacional antes de decidir se pull_delta é necessário.
async function checkCollectionRevisions(farmId, collections, userName) {
  return await apiCall({
    action: "revision_check",
    farm_id: farmId,
    collections: Array.isArray(collections) ? collections : [collections],
  }, userName || "revision-check");
}

// Pull one collection from server
async function pullCollection(farmId, collection, userName, options = {}) {
  const forceFull = !!options?.forceFull;
  const localCursor = forceFull ? null : ((await DB.get(cursorKey(farmId, collection))) || null);
  const res = await apiCall({ action:"pull", farm_id:farmId, collection, client_cursor: localCursor, app_version: APP_VERSION }, userName);
  if (res?.cursor) await DB.set(cursorKey(farmId, collection), res.cursor);
  return res;
}

// v3.9.03: pullFarmBundle só pode ser chamado pelo bootstrap protegido.
// Qualquer caller sem source:"protected_bootstrap" é bloqueado e recebe skipped.
async function pullFarmBundle(farmId, userName, collections = FARM_COLLECTIONS, opts = {}) {
  if (opts?.source !== "protected_bootstrap") {
    try {
      console.warn("[v3.9.03] pullFarmBundle bloqueado fora do bootstrap protegido", {
        farmId,
        source: opts?.source || "unknown",
        stack: new Error().stack?.split("\n")[2]?.trim() || "",
      });
    } catch (_) {}
    return { skipped: true, reason: "farm_bundle_blocked_non_bootstrap" };
  }
  // v3.9.04: incluir bootstrap_source no payload — gate de rawApiCall diferencia
  // o bundle legítimo de qualquer caller externo que escape ao check de opts.
  // v3.9.05: timeout de 3 minutos — bundle com 28 collections + gzip pode
  // demorar >8s em HostGator. rawApiCall direto evita ensureServerInstall extra.
  // Timeout padrão continua 8000ms para todas as outras chamadas.
  return await rawApiCall({
    action: "pull_farm_bundle",
    farm_id: farmId,
    collections,
    app_version: APP_VERSION,
    bootstrap_source: "protected_bootstrap",
  }, userName, 180000);
}

async function pullCollectionDelta(farmId, collection, sinceUpdatedAt = "", limit = 200, userName = "diagnostic-delta") {
  // v3.0.00: helper usado pelo Diagnóstico Delta e pelo piloto automático de orders.
  // Mantém compatibilidade defensiva com a assinatura antiga: (farmId, collection, userName, updatedAfter).
  let cursor = sinceUpdatedAt;
  let safeLimitInput = limit;
  let callerUserName = userName;
  if (arguments.length === 4 && typeof limit === "string" && !/^\d+$/.test(String(limit))) {
    callerUserName = sinceUpdatedAt || "diagnostic-delta";
    cursor = limit;
    safeLimitInput = 200;
  }
  if (!farmId || !collection) {
    throw new Error("farmId e collection obrigatórios");
  }
  const safeLimit = Math.max(1, Math.min(500, Number(safeLimitInput) || 200));
  return await apiCall({
    action: "pull_delta",
    farm_id: farmId,
    collection: String(collection),
    since_updated_at: String(cursor || ""),
    limit: safeLimit,
    app_version: APP_VERSION,
  }, callerUserName);
}


function parseProtectedTs(item) {
  const candidates = [item?.deletedAt, item?.deleted_at, item?.updatedAt, item?.createdAt];
  return candidates.reduce((max, value) => {
    const n = Date.parse(value || '');
    return Number.isFinite(n) ? Math.max(max, n) : max;
  }, 0);
}

function protectedIdentityKey(item, collection = '') {
  if (!item || typeof item !== 'object') return '';
  const col = String(collection || '').trim();
  const clientId = String(item?.clientId || '').trim();
  const id = String(item?.id || '').trim();
  const recordNumber = String(item?.recordNumber || item?.number || item?.code || '').trim().toLowerCase();
  const farmId = String(item?.farmId || '').trim();
  if (clientId) return `${col}:client:${clientId}`;
  if (id) return `${col}:id:${id}`;
  if (recordNumber) return `${col}:record:${farmId}:${recordNumber}`;
  return '';
}

function normalizeOrderStatusText(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function orderStatusStrength(value = '') {
  const status = normalizeOrderStatusText(value);
  if (!status) return 0;
  if (status.includes('neg')) return 6;
  if (status.includes('cancel')) return 6;
  if (status.includes('conclu') || status.includes('receb')) return 5;
  if (status.includes('transito') || status.includes('envi')) return 4;
  if (status.includes('aprov') || status.includes('parcial')) return 3;
  if (status.includes('analis') || status.includes('cot')) return 2;
  if (status.includes('pend') || status.includes('criad')) return 1;
  return 0;
}

function orderItemsStatusStrength(items = []) {
  return (Array.isArray(items) ? items : []).reduce((max, item) => Math.max(max, orderStatusStrength(item?.itemStatus || item?.status)), 0);
}

function orderWorkflowStrength(item = {}) {
  if (!item || typeof item !== 'object') return 0;
  return Math.max(orderStatusStrength(item?.status), orderItemsStatusStrength(item?.items));
}

function applyOrderWorkflowFloor(merged = {}, localItem = {}, serverItem = {}) {
  const localStrength = orderWorkflowStrength(localItem);
  const serverStrength = orderWorkflowStrength(serverItem);
  const mergedStrength = orderWorkflowStrength(merged);
  const winner = localStrength >= serverStrength ? localItem : serverItem;
  const winnerStrength = Math.max(localStrength, serverStrength);
  if (winnerStrength > 0 && winnerStrength > mergedStrength) {
    return {
      ...merged,
      ...winner,
      serverRevision: Math.max(Number(merged?.serverRevision || 0), Number(localItem?.serverRevision || 0), Number(serverItem?.serverRevision || 0)),
    };
  }
  return {
    ...merged,
    serverRevision: Math.max(Number(merged?.serverRevision || 0), Number(localItem?.serverRevision || 0), Number(serverItem?.serverRevision || 0)),
  };
}

function mergeProtectedItem(localItem, serverItem, collection = '') {
  if (!localItem) return serverItem;
  if (!serverItem) return localItem;
  // v2.221.33: UDT local em item já confirmado não pode ser apagado por
  // snapshot atrasado. Só ACK/rejeição explícita do servidor deve limpar.
  const localHasUDT = !!(!localItem?.pendingLocalCreate
    && !String(localItem?.recordNumber || '').toUpperCase().endsWith('-PRV')
    && (localItem?.pendingLocalUpdate || localItem?.updatePending || (localItem?.pendingLocalChange && localItem?.pendingOperationId)));
  const serverStillPending = !!(serverItem?.pendingLocalUpdate || serverItem?.updatePending || serverItem?.pendingLocalChange);
  const localOp = String(localItem?.pendingOperationId || localItem?.localOperationId || '').trim();
  const serverOp = String(serverItem?.serverAckOperationId || serverItem?.ackedOperationId || serverItem?.operationId || serverItem?.pendingOperationId || '').trim();
  const serverHasExplicitAck = !!(serverItem?.serverAckAt || serverItem?.serverAckOperationId || serverItem?.ackedOperationId);
  const serverAckedSameOp = !!(localHasUDT
    && !serverStillPending
    && serverHasExplicitAck
    && (!localOp || !serverOp || localOp === serverOp));
  if (localHasUDT && !serverAckedSameOp) {
    return {
      ...serverItem,
      ...localItem,
      serverRevision: Math.max(Number(localItem?.serverRevision || 0), Number(serverItem?.serverRevision || 0)),
      pendingLocalUpdate: true,
      pendingLocalChange: true,
      syncStatus: 'pending',
    };
  }
  // v2.221.19: servidor tem autoridade explícita quando confirmou a criação PRV.
  // Múltiplos sinais de PRV local = redundância defensiva.
  const localHasPRV = !!(localItem?.pendingLocalCreate
    || String(localItem?.recordNumber || '').toUpperCase().endsWith('-PRV')
    || (localItem?.syncStatus === 'pending' && !localItem?.serverConfirmed));
  const serverConfirmedCreate = !!(serverItem?.serverConfirmed
    && serverItem?.pendingLocalCreate === false
    && serverItem?.syncStatus === 'synced');
  if (localHasPRV && serverConfirmedCreate) {
    return { ...localItem, ...serverItem, pendingLocalCreate: false, pendingLocalChange: false, syncStatus: 'synced' };
  }
  const localTs = parseProtectedTs(localItem);
  const serverTs = parseProtectedTs(serverItem);
  const preferred = localTs >= serverTs ? localItem : serverItem;
  const secondary = preferred === localItem ? serverItem : localItem;
  const merged = { ...secondary, ...preferred };
  if (String(collection || '').trim() === 'orders') {
    return applyOrderWorkflowFloor(merged, localItem, serverItem);
  }
  return merged;
}

function mergeProtectedVisibleWithServer(localVisible, serverRaw, collection = '') {
  const locArr = Array.isArray(localVisible) ? localVisible : [];
  const srvArr = Array.isArray(serverRaw) ? serverRaw : [];
  const merged = [];
  const consumedServer = new Set();
  let needsPush = false;
  locArr.forEach((locItem) => {
    const locKey = protectedIdentityKey(locItem, collection);
    const serverIndex = srvArr.findIndex((srvItem, index) => {
      if (consumedServer.has(index)) return false;
      const srvKey = protectedIdentityKey(srvItem, collection);
      return locKey && srvKey && locKey === srvKey;
    });
    if (serverIndex < 0) {
      merged.push(locItem);
      needsPush = true;
      return;
    }
    consumedServer.add(serverIndex);
    const chosen = mergeProtectedItem(locItem, srvArr[serverIndex], collection);
    merged.push(chosen);
    if (stableJson(chosen) !== stableJson(srvArr[serverIndex])) needsPush = true;
  });
  srvArr.forEach((srvItem, index) => {
    if (!consumedServer.has(index)) merged.push(srvItem);
  });
  return { raw: merged, visible: stripDeletedItems(merged), needsPush };
}

async function refreshCollectionFromServer(farmId, collection, userName, options = {}) {
  const forceFull = options?.forceFull !== false;
  const localKey = `fz:${farmId}:${collection}`;
  const shadow = shadowKey(farmId, collection);
  const opKey = opsKey(farmId, collection);
  const pendKey = pendingKey(farmId, collection);
  const srv = await pullCollection(farmId, collection, userName, { forceFull });
  const srvRaw = Array.isArray(srv?.data) ? srv.data : [];
  const localBefore = await DB.get(localKey);
  const localVisible = Array.isArray(localBefore) ? localBefore : [];
  const pendingOps = (await DB.get(opKey)) || [];
  const pendingCount = Number((await DB.get(pendKey)) || 0);
  const hasPendingLocalWrite = (Array.isArray(pendingOps) && pendingOps.length > 0) || pendingCount > 0;

  // Proteção para coleções operacionais críticas, especialmente Compras:
  // um pull direto não pode apagar/reverter um registro local mais novo ou pendente
  // apenas porque o servidor ainda devolveu um snapshot antigo/parcial.
  const protectedCollections = new Set(["orders", "services", "occurrences", "vet", "inspections"]);
  const merged = protectedCollections.has(String(collection || ""))
    ? mergeProtectedVisibleWithServer(localVisible, srvRaw, collection)
    : null;
  const shouldPreserveLocal = !!merged && (hasPendingLocalWrite || merged.needsPush);
  const rawToStore = shouldPreserveLocal ? merged.raw : srvRaw;
  const visible = stripDeletedItems(rawToStore);

  await DB.set(localKey, visible);
  // v2.242.06: shadow sempre recebe rawToStore. Quando shouldPreserveLocal=true,
  // rawToStore = merged.raw e preserva tombstones pendentes locais. Quando
  // shouldPreserveLocal=false, rawToStore === srvRaw, mantendo o comportamento.
  await DB.set(shadow, rawToStore);
  setLiveCollectionCache(localKey, visible);
  setLiveCollectionCache(shadow, rawToStore);
  await DB.set(verKey(farmId, collection), srv?.version || 0);
  if (srv?.cursor) await DB.set(cursorKey(farmId, collection), srv.cursor);
  if (shouldPreserveLocal) {
    await DB.set(pendKey, Math.max(pendingCount, Array.isArray(pendingOps) ? pendingOps.length : 0, 1));
  } else {
    await DB.set(opKey, []);
    await DB.set(pendKey, 0);
    try { await SyncQueue.clearEntity(`${farmId}:${collection}`); } catch(e) {}
  }
  window.dispatchEvent(new CustomEvent("fz:pending:changed"));
  window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
  return { ...srv, data: rawToStore, protectedLocalWrite: shouldPreserveLocal };
}

function isFallbackOnlyAuthAccountsPayload(data) {
  const list = Array.isArray(data) ? data.filter(item => item && !item._deleted && !item.deletedAt && !item.deleted_at) : [];
  if (list.length !== 1) return false;
  const account = list[0] || {};
  const username = String(account.username || "").trim().toLowerCase();
  const role = String(account.role || "").trim().toLowerCase();
  const id = String(account.id || account.clientId || "").trim().toLowerCase();
  const isDefaultAdminIdentity = username === "admin" || id === "acc_admin";
  return isDefaultAdminIdentity
    && (!role || role === "admin")
    && account.defaultPasswordBlocked !== true;
}

// Push/pull global key (farms list, auth accounts)
async function pushGlobal(key, data, userName) {
  if (key === "auth:accounts" && isFallbackOnlyAuthAccountsPayload(data)) {
    return { ignored: true, reason: "blocked_local_auth_accounts_fallback_only" };
  }
  const result = await apiCall({ action:"push_global", key, data }, userName);
  invalidateGlobalPullCache(key);
  return result;
}
async function pullGlobal(key, userName, options = {}) {
  const cacheKey = globalPullCacheKey(key, userName);
  const force = !!options?.force;
  const ttl = Number(options?.ttlMs || globalPullTtlForKey(key));
  const now = Date.now();
  if (!force && ttl > 0) {
    const cached = globalPullCache.get(cacheKey);
    if (cached && (now - Number(cached.at || 0)) < ttl) {
      return cached.value;
    }
    const inFlight = globalPullInFlight.get(cacheKey);
    if (inFlight) return inFlight;
  }
  const promise = apiCall({ action:"pull_global", key }, userName)
    .then((value) => {
      globalPullCache.set(cacheKey, { at: Date.now(), value });
      return value;
    })
    .finally(() => { globalPullInFlight.delete(cacheKey); });
  if (!force && ttl > 0) globalPullInFlight.set(cacheKey, promise);
  return await promise;
}

export { apiCall, pushCollection, pushOrderOps, pushOperationalOps, checkCollectionRevisions, pullCollection, pullCollectionDelta, pullFarmBundle, refreshCollectionFromServer, pushGlobal, pullGlobal, ensureServerInstall, mergeProtectedVisibleWithServer, deltaCursorKey, checkDeviceBootstrapState, startDeviceBootstrapState, completeDeviceBootstrapState, failDeviceBootstrapState };
